import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Twitter, Globe, ExternalLink, User, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    className={className}
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// OnlyFans icon component  
const OnlyFansIcon = ({ size = 24, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
  >
    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12zm0 10a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
  </svg>
);

// HotPeek icon component  
const HotPeekIcon = ({ size = 24, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

const CreatorLandingPage = () => {
  const { slug } = useParams();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate a simple visitor ID for tracking unique visitors
  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      visitorId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("visitor_id", visitorId);
    }
    return visitorId;
  };

  // Track page view
  const trackPageView = async () => {
    try {
      await fetch(`${API}/public/track-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          creator_username: slug,
          page_type: "landing",
          visitor_id: getVisitorId()
        })
      });
    } catch (err) {
      // Silently ignore tracking errors
      console.debug("View tracking failed:", err);
    }
  };

  useEffect(() => {
    fetchCreator();
  }, [slug]);

  // Track view after creator is loaded successfully
  useEffect(() => {
    if (creator && !error) {
      trackPageView();
    }
  }, [creator]);

  const fetchCreator = async () => {
    try {
      const response = await fetch(`${API}/creator/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError("This page is not available");
        } else {
          setError("Something went wrong");
        }
        return;
      }
      const data = await response.json();
      setCreator(data);
    } catch (err) {
      setError("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Page Not Found</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <Link 
            to="/" 
            className="text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const links = creator?.links || {};
  const hasHotPeekLink = creator?.show_hotpeek_link && creator?.hotpeek_profile_url;
  const hasLinks = links.instagram || links.twitter || links.tiktok || links.onlyfans || links.website || hasHotPeekLink;

  // Helper to get correct image data URI
  const getImageSrc = (base64Data) => {
    if (!base64Data) return null;
    if (base64Data.startsWith("data:")) return base64Data;
    
    // Detect image type from base64 signature
    if (base64Data.startsWith("UklGR")) {
      return `data:image/webp;base64,${base64Data}`;
    } else if (base64Data.startsWith("/9j/")) {
      return `data:image/jpeg;base64,${base64Data}`;
    } else if (base64Data.startsWith("iVBOR")) {
      return `data:image/png;base64,${base64Data}`;
    }
    // Default to jpeg
    return `data:image/jpeg;base64,${base64Data}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Profile Image */}
        <div className="flex justify-center mb-6">
          {creator?.profile_image ? (
            <img
              src={getImageSrc(creator.profile_image)}
              alt={creator.name}
              loading="eager"
              decoding="async"
              className="w-32 h-32 rounded-full object-cover border-4 border-white/20 shadow-xl"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center border-4 border-white/20 shadow-xl">
              <User className="w-16 h-16 text-white/80" />
            </div>
          )}
        </div>

        {/* Name & Headline */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {creator?.name}
          </h1>
          <p className="text-xl text-gray-300">
            {creator?.headline || `Follow @${slug}`}
          </p>
          {creator?.description && (
            <p className="text-gray-400 mt-4 max-w-sm mx-auto">
              {creator.description}
            </p>
          )}
        </div>

        {/* Social Links */}
        {hasLinks && (
          <div className="space-y-3">
            {/* HotPeek Profile Link - shown first when enabled */}
            {hasHotPeekLink && (
              <Link
                to={creator.hotpeek_profile_url}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-between w-full px-6 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-purple-500/25 transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <HotPeekIcon size={24} />
                    <span>HotPeek Profile</span>
                  </div>
                  <ExternalLink size={20} />
                </motion.div>
              </Link>
            )}

            {links.onlyfans && (
              <motion.a
                href={links.onlyfans.startsWith("http") ? links.onlyfans : `https://onlyfans.com/${links.onlyfans}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-between w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-cyan-500/25 transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <OnlyFansIcon size={24} />
                  <span>OnlyFans</span>
                </div>
                <ExternalLink size={20} />
              </motion.a>
            )}

            {links.instagram && (
              <motion.a
                href={links.instagram.startsWith("http") ? links.instagram : `https://instagram.com/${links.instagram.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-between w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-purple-500/25 transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <Instagram size={24} />
                  <span>Instagram</span>
                </div>
                <ExternalLink size={20} />
              </motion.a>
            )}

            {links.twitter && (
              <motion.a
                href={links.twitter.startsWith("http") ? links.twitter : `https://twitter.com/${links.twitter.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-between w-full px-6 py-4 bg-black text-white rounded-xl font-semibold shadow-lg hover:shadow-gray-500/25 transition-shadow border border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <Twitter size={24} />
                  <span>Twitter / X</span>
                </div>
                <ExternalLink size={20} />
              </motion.a>
            )}

            {links.tiktok && (
              <motion.a
                href={links.tiktok.startsWith("http") ? links.tiktok : `https://tiktok.com/@${links.tiktok.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-between w-full px-6 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-gray-500/25 transition-shadow border border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <TikTokIcon size={24} />
                  <span>TikTok</span>
                </div>
                <ExternalLink size={20} />
              </motion.a>
            )}

            {links.website && (
              <motion.a
                href={links.website.startsWith("http") ? links.website : `https://${links.website}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-between w-full px-6 py-4 bg-white/10 text-white rounded-xl font-semibold shadow-lg hover:bg-white/20 transition-colors border border-white/20"
              >
                <div className="flex items-center gap-3">
                  <Globe size={24} />
                  <span>Website</span>
                </div>
                <ExternalLink size={20} />
              </motion.a>
            )}
          </div>
        )}

        {/* Powered by - Only shown if branding is enabled */}
        {creator.show_platform_branding !== false && (
          <div className="mt-12 text-center">
            <Link 
              to="/"
              className="text-sm text-gray-500 hover:text-gray-400 transition-colors"
            >
              Powered by <span className="font-semibold">HotPeek</span>
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CreatorLandingPage;
